type ChangeEntry<T> = {
    id: string;
    action: () => Promise<T>;
    resolve: (_value: T) => void;
    reject: (_reason: any) => void;
};

const QUEUE: ChangeEntry<any>[] = [];
let processing: boolean         = false;

// The first version of debouncing:
// When the debounce function is called the first time the action will be triggered immediately
// For a burst of calls the last one will also be called and the intermidiates will be dropped
export async function debounce<T>(id: string, action: () => Promise<T>): Promise<T> {
    // remove entries with this id
    const index = QUEUE.findIndex(entry => entry.id === id);
    if (index !== -1) {
        QUEUE.splice(index, 1);
    }

    // Wrap the action in a typed Promise and add it to the queue
    // eslint-disable-next-line no-async-promise-executor
    return new Promise<T>(async (resolve, reject) => {
        QUEUE.push({
            id,
            action,
            resolve,
            reject,
        });

        if (!processing && QUEUE.length > 0) {
            processing = true;

            while (QUEUE.length > 0) {
                const {
                    action,
                    resolve,
                    reject,
                } = QUEUE.shift()!; // Take the next task
                try {
                    const t = await action(); // Execute the action
                    resolve(t); // ...and resolve the promise with its result
                } catch (error) {
                    reject(error); // Reject the promise on error
                }
            }

            processing = false;
        }
    });
}

// The second form of debouncing:
// For a burst of trigger calls only the last will be call the payload.
// The timeout will determine what is considered as an end of burst
export class Debouncer<T> {
    private readonly delay: number;
    private readonly payloadFunc: (arg: T) => Promise<void>;
    private readonly errorFunc: undefined | ((error: any) => Promise<void>) = undefined;
    private timeout: ReturnType<typeof setTimeout> | null                   = null;

    public constructor(delay: number, payloadFunc: (arg: T) => Promise<void>, errorFunc: undefined | ((error: any) => Promise<void>) = undefined) {
        this.delay       = delay;
        this.payloadFunc = payloadFunc;
        this.errorFunc   = errorFunc;
    }

    public trigger(arg: T): void {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
        this.timeout = setTimeout(async () => {
            this.timeout = null;
            try {
                await this.payloadFunc(arg);
            } catch (error) {
                await this.errorFunc?.(error);
            }
        }, this.delay);
    }
}
